import React, { useState } from 'react';
import { Button, InputGroup, FormControl, ListGroup, Spinner, Badge } from 'react-bootstrap';
import { FaPlus, FaCheck } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import { formatCurrency } from '../../../../../../../../../../../../../../../../utils/functions';
import FilterDropdown from '../../../../../../../FilterDropdown/FilterDropdown';
import './ItemSelectorSection.scss';

interface Item {
  item_id: number;
  item_name: string;
  label?: string;
  price: number;
  price_delivery?: number;
  price_online?: number;
  price_desk?: number;
}

interface ItemSelectorSectionProps {
  showItemSelector: boolean;
  setShowItemSelector: (show: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredItems: Item[];
  loadingItems: boolean;
  onItemClick: (item: Item) => void;
  onMultipleItemsAdd: (items: Item[]) => void;
  itemsCount: number;
  allCategories: any[];
  selectedCategories: any[];
  onCategoriesChange: (categories: any[]) => void;
  onAddAllFromCategory: (categoryItems: Item[]) => void;
  availableItems: Item[];
}

const ItemSelectorSection: React.FC<ItemSelectorSectionProps> = ({
  showItemSelector,
  setShowItemSelector,
  searchQuery,
  setSearchQuery,
  filteredItems,
  loadingItems,
  onItemClick,
  onMultipleItemsAdd,
  itemsCount,
  allCategories,
  selectedCategories,
  onCategoriesChange,
  onAddAllFromCategory,
  availableItems
}) => {
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCancelSearch = () => {
    setShowItemSelector(false);
    setSearchQuery('');
    setSelectedItems([]);
  };

  const handleItemSelect = (item: Item) => {
    const isAlreadySelected = selectedItems.some(selected => selected.item_id === item.item_id);
    
    if (isAlreadySelected) {
      setSelectedItems(selectedItems.filter(selected => selected.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleRemoveSelectedItem = (itemId: number) => {
    setSelectedItems(selectedItems.filter(item => item.item_id !== itemId));
  };

  const handleConfirmSelection = () => {
    if (selectedItems.length > 0) {
      onMultipleItemsAdd(selectedItems);
      setSelectedItems([]);
      setShowItemSelector(false);
      setSearchQuery('');
    }
  };

  const isItemSelected = (itemId: number) => {
    return selectedItems.some(item => item.item_id === itemId);
  };

  const handleAddAllFromSelectedCategories = () => {
    if (selectedCategories.length === 0) return;
    
    const selectedCategoryIds = selectedCategories.map(cat => cat.value);
    const categoryItems = availableItems.filter((item: any) => 
      selectedCategoryIds.includes(item.category_id)
    );
    
    if (categoryItems.length > 0) {
      onAddAllFromCategory(categoryItems);
    }
  };

  return (
    <div className="item-selector-section">
      {/* Section Header */}
      <div className="items-header">
        
        {!showItemSelector && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowItemSelector(true)}
            className="add-item-btn"
            type="button"
          >
            <FaPlus className="me-1" /> Agregar Item
          </Button>
        )}
      </div>

      {/* Multi-Selection Interface */}
      {showItemSelector && (
        <div className="item-selector">
          {/* Search Input */}
          <div className="search-container">
            <div className="search-row">
              <div className="search-wrapper">
                <InputGroup className="search-input">
                  <InputGroup.Text className="search-icon">
                  </InputGroup.Text>
                  <FormControl
                    type="text"
                    placeholder="Busca por nombre del item..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                    className="search-field"
                    autoComplete="off"
                  />
                </InputGroup>
              </div>
              
              {/* Category Filter Dropdown */}
              <div className="category-filter-wrapper">
                <FilterDropdown
                  options={allCategories.map(cat => ({
                    value: cat.category_id,
                    label: cat.category_name
                  }))}
                  selectedItems={selectedCategories}
                  onChange={onCategoriesChange}
                  placeholder="Filtrar por categorías"
                />
              </div>
            </div>
            
            {/* Add All from Category Button */}
            {selectedCategories.length > 0 && (
              <div className="add-all-button-row">
                <Button
                  variant="none"
                  size="sm"
                  onClick={handleAddAllFromSelectedCategories}
                  className="add-all-category-btn"
                  type="button"
                >
                  <FaPlus className="me-1" />
                  Agregar todos los ítems de {selectedCategories.length === 1 ? 'la categoría' : 'las categorías'}
                </Button>
              </div>
            )}
          </div>

          {/* Selected Items Badges */}
          {selectedItems.length > 0 && (
            <div className="selected-items-section">
              <div className="selected-items-badges">
                {selectedItems.map((item) => (
                  <Badge 
                    key={item.item_id} 
                    className="selected-item-badge"
                    bg="secondary"
                  >
                    <span className="badge-text">{item.item_name}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="badge-remove-btn"
                      onClick={() => handleRemoveSelectedItem(item.item_id)}
                      type="button"
                    >
                      <X size={12} />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          <div className="search-results">
            {loadingItems ? (
              <div className="loading-state">
                <Spinner animation="border" size="sm" className="me-2" />
                Buscando...
              </div>
            ) : filteredItems.length > 0 ? (
              <>
                {searchQuery && (
                  <div className="results-count">
                    {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
                  </div>
                )}
                
                <ListGroup className="items-list">
                  {filteredItems.map((item) => (
                    <ListGroup.Item
                      key={item.item_id}
                      action
                      onClick={() => handleItemSelect(item)}
                      className={`item-option ${isItemSelected(item.item_id) ? 'selected' : ''}`}
                    >
                      <div className="item-content">
                        <div className="item-main-info">
                          <div className="item-name-container">
                            <span className="item-name">{item.item_name}</span>
                            {item.label && item.label !== item.item_name && (
                              <span className="item-label"> • {item.label}</span>
                            )}
                          </div>
                        </div>
                        <div className="item-price">
                          {item.price > 0 ? formatCurrency(item.price) : (
                            <span className="free-price">Gratis</span>
                          )}
                        </div>
                        {isItemSelected(item.item_id) && (
                          <div className="selected-indicator">
                            <FaCheck />
                          </div>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            ) : searchQuery ? (
              <div className="no-results">
                Sin resultados para "{searchQuery}"
              </div>
            ) : (
              <div className="empty-search">
                Escribe para buscar items...
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="selector-actions">
            <Button
              variant="secondary"
              onClick={handleCancelSearch}
              className="cancel-btn"
              type="button"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSelection}
              className="confirm-btn"
              type="button"
              disabled={selectedItems.length === 0}
            >
              <FaCheck className="me-1" />
              Agregar {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {itemsCount === 0 && !showItemSelector && (
        <div className="empty-items-state">
          <span className="empty-text">No hay items agregados.</span>
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowItemSelector(true)}
            type="button"
            className="add-first-btn"
          >
            Agregar primer item
          </Button>
        </div>
      )}
    </div>
  );
};

export default ItemSelectorSection;
